// src/services/UserApiService.ts
import { BaseApiService } from "./ApiService";
import { apiConfig } from "../config/apiConfig";

export interface UserStatusResponse {
  isSuccess: boolean;
  message?: string;
  membership: 'PRO' | 'FREE';
  rateLimit: {
    nextAvailableTime: string;
    cooldownRemaining?: number;
  } | null;
  /**
   * The capture allowance, as the server sees it.
   *
   * Optional because an older deployment does not send it — and the fallback matters: a missing
   * field leaves `proRequired` undefined, the pre-flight lets the capture through, and the
   * server refuses it with a 402. That is the old behaviour, not a new failure, and it is the
   * right way round: a stale server must not lock out a paying user.
   */
  captureQuota?: {
    used: number;
    limit: number | null;
    remaining: number | null;
    exhausted: boolean;
    /** No free allowance: this account must upgrade before it can capture at all. */
    proRequired?: boolean;
  };
}


class UserApiService extends BaseApiService {
  private statusCache = new Map<string, {
    data: UserStatusResponse;
    timestamp: number;
  }>();
  private pendingRequests = new Map<string, Promise<UserStatusResponse>>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  private getCacheKey(userId: string): string {
    return `user_status_${userId}`;
  }

  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  async getUserStatus(userId: string, forceRefresh = false): Promise<UserStatusResponse> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    const cacheKey = this.getCacheKey(userId);

    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cached = this.statusCache.get(cacheKey);
      if (cached && this.isCacheValid(cached.timestamp)) {
        return cached.data;
      }
    }

    // Check if there's already a pending request for this user
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    // Create new request
    const requestPromise = this.executeWithRetry(async () => {
      const response = await this.makeRequest<UserStatusResponse>({
        method: 'GET',
        url: this.buildUrl(apiConfig.endpoints.userStatus(userId)),
        timeout: apiConfig.timeout.default
      }, `Get user status for ${userId}`, `user_status_${userId}`);

      // Validate response
      if (!response.data || !response.data.isSuccess) {
        throw new Error(response.data?.message || "Failed to get user status");
      }

      // Cache the result
      this.statusCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });

      return response.data;
    }, `Get user status for ${userId}`);

    // Store the pending request
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Remove from pending requests when done
      this.pendingRequests.delete(cacheKey);
    }
  }

  async checkUserStatus(userId: string): Promise<{
    canPerformAction: boolean;
    statusMessage: string | null;
    userStatus?: UserStatusResponse;
  }> {
    if (!userId) {
      return {
        canPerformAction: false,
        statusMessage: "User ID is required. Please restart the plugin.",
      };
    }

    try {
      const userStatus = await this.getUserStatus(userId);

      if (!userStatus.isSuccess) {
        return {
          canPerformAction: false,
          statusMessage: "Unable to verify user status. Please try again later.",
          userStatus,
        };
      }

      const isPro = userStatus.membership === 'PRO';

      // PRO users have unlimited access
      if (isPro) {
        return {
          canPerformAction: true,
          statusMessage: null,
          userStatus,
        };
      }

      /*
       * CAPTURING IS A PAID FEATURE, so a non-PRO account is stopped here, before the request.
       *
       * The pre-flight exists so the user is told BEFORE a capture is attempted rather than after
       * — `/user/:userId/status` reports `captureQuota.proRequired` for exactly this. Without
       * this branch the next one returns `canPerformAction: true` for a free account, the tab
       * issues the capture, and the server refuses it with a 402: the regression
       * `routes/status-quota.test.ts` was written to prevent, in a new shape.
       *
       * Ahead of the rate-limit branch because there is nothing to wait for. A free account has
       * no `nextAvailableTime` to report, so that branch would fall through to "allowed".
       */
      if (userStatus.captureQuota?.proRequired) {
        return {
          canPerformAction: false,
          statusMessage: 'Capturing is a PRO feature. Upgrade to import pages into Figma.',
          userStatus,
        };
      }

      // Anyone left who is still metered: check rate limit
      if (userStatus.rateLimit?.nextAvailableTime) {
        const nextAvailable = new Date(userStatus.rateLimit.nextAvailableTime);
        const now = new Date();

        if (nextAvailable > now) {
          const timeRemaining = Math.ceil((nextAvailable.getTime() - now.getTime()) / (1000 * 60));
          const hours = Math.floor(timeRemaining / 60);
          const minutes = timeRemaining % 60;

          let timeStr = '';
          if (hours > 0) timeStr += `${hours}h `;
          if (minutes > 0 || hours === 0) timeStr += `${minutes}m`;

          return {
            canPerformAction: false,
            statusMessage: `Rate limit active. Please wait ${timeStr}. Upgrade to PRO for unlimited access.`,
            userStatus,
          };
        }
      }

      return {
        canPerformAction: true,
        statusMessage: null,
        userStatus,
      };
    } catch {
      // Error sent to API via logger - no console output
      return {
        canPerformAction: true,
        statusMessage: "Could not verify user status. Proceeding with caution.",
      };
    }
  }

  async recordImportUsage(figmaUID: string, figmaUserName: string = ""): Promise<void> {
    if (!figmaUID) {
      throw new Error("Figma user ID is required");
    }

    try {
      // No membership on the wire — see services/client-entitlement.test.ts. This route resolves
      // the tier from the database like every other.
      const queryParams = new URLSearchParams({
        figmaUID: figmaUID,
        figmaUserName: figmaUserName || "",
      });

      // The response carries `isSuccess` and, for a rate-limited FREE user,
      // `rateLimit.nextAvailableTime`. It has never carried a remaining-import count — the field
      // this used to declare and return did not exist on any response the server sends.
      const response = await this.makeRequest<{
        isSuccess: boolean;
        message?: string;
      }>({
        method: 'POST',
        url: `${this.buildUrl(apiConfig.endpoints.recordImportUsage)}?${queryParams.toString()}`,
        data: {},
        headers: { "Content-Type": "application/json" },
        timeout: apiConfig.timeout.default
      }, "Record import usage", `record_usage_${figmaUID}`);

      if (!response.data || !response.data.isSuccess) {
        throw new Error(response.data?.message || "Server reported an error recording import usage");
      }

      // Clear cache for this user since their usage changed
      const cacheKey = this.getCacheKey(figmaUID);
      this.statusCache.delete(cacheKey);
    } catch {
      // Error sent to API via logger - no console output
    }
  }

  clearUserCache(userId?: string): void {
    if (userId) {
      const cacheKey = this.getCacheKey(userId);
      this.statusCache.delete(cacheKey);
      this.pendingRequests.delete(cacheKey);
    } else {
      this.statusCache.clear();
      this.pendingRequests.clear();
    }
  }

  getCacheStats(): { cacheSize: number; pendingRequests: number } {
    return {
      cacheSize: this.statusCache.size,
      pendingRequests: this.pendingRequests.size
    };
  }
}

// Export singleton instance
export const userApiService = new UserApiService();